import React from 'react';
import { AlertTriangle, Clock, User, FileText, UserCircle, Bell } from 'lucide-react';

const NotificationsTab = ({ notifications, onRecordSelect, loading, error }) => {
    if (loading) {
        return (
            <div className="text-center p-8 bg-white rounded-lg shadow-md">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div>
                <p className="mt-2 text-gray-600">Loading alerts...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="text-center p-8 bg-red-50 text-red-700 rounded-lg shadow-md">
                <p>{error}</p>
            </div>
        );
    }

    if (notifications.length === 0) {
        return (
            <div className="text-center py-8 bg-white rounded-lg shadow-md p-6">
                <p className="text-gray-500">No upcoming events or new assignments.</p>
            </div>
        );
    }

    const formatTime = (timeString) => {
        if (!timeString) return '';
        if (timeString === 'Now') return 'Now';
        const [hour, minute] = timeString.split(':');
        const hourNum = parseInt(hour, 10);
        const ampm = hourNum >= 12 ? 'PM' : 'AM';
        const formattedHour = hourNum % 12 || 12;
        return `${formattedHour}:${minute} ${ampm}`;
    };

    const formatDate = (dateString) => new Date(dateString).toLocaleDateString();

    const assignments = notifications.filter(n => n.isAssignment);
    const upcomingEvents = notifications.filter(n => !n.isOverdue && !n.isAssignment);
    const overdueEvents = notifications.filter(n => n.isOverdue && !n.isAssignment);

    const EventCard = ({ event }) => {
        const { isOverdue, isAssignment } = event;
        let cardClass = "border rounded-lg p-4 ";
        let iconColor = "";
        let timeText = "";

        if (isAssignment) {
            cardClass += "bg-blue-50 border-blue-200";
            iconColor = "text-blue-700";
            timeText = "New Assignment";
        } else if (isOverdue) {
            cardClass += "bg-red-50 border-red-200";
            iconColor = "text-red-700";
            timeText = `Was due on ${formatDate(event.scheduledDate)} at ${formatTime(event.scheduledTime)}`;
        } else {
            cardClass += "bg-yellow-50 border-yellow-200";
            iconColor = "text-yellow-800";
            timeText = `Due today at ${formatTime(event.scheduledTime)}`;
        }

        return (
            <div key={event.commentId || event.recordId} className={cardClass}>
                <div className="flex justify-between items-start gap-4">
                    <div className="flex-1">
                        <div className="flex items-center flex-wrap gap-x-4 gap-y-1 mb-2">
                            <span className="font-bold text-lg text-gray-800">{event.ptName}</span>
                            <span className={`text-sm flex items-center gap-1 font-semibold ${iconColor}`}>
                                {isAssignment ? <Bell className="w-4 h-4" /> : <Clock className="w-4 h-4" />}
                                {timeText}
                            </span>
                        </div>
                        <p className="text-sm text-gray-700 mb-2">{event.text}</p>
                        <div className="flex items-center gap-4 text-xs text-gray-500 mt-2">
                            <span className="flex items-center gap-1 font-medium"><UserCircle className="w-4 h-4" />Assigned to: {event.assignedCollector?.username || 'Unassigned'}</span>
                            {!isAssignment && <span className="flex items-center gap-1"><User className="w-3 h-3" />Task by: {event.author?.username || 'Unknown'}</span>}
                        </div>
                    </div>
                    <div className="shrink-0">
                        <button
                            onClick={() => onRecordSelect(event.recordId)}
                            className="px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white text-xs rounded-md transition-colors flex items-center gap-1 justify-center"
                        >
                            <FileText className="w-3 h-3" />
                            View Record
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
                <AlertTriangle className="w-6 h-6 mr-2 text-red-600" />
                Actionable Alerts ({notifications.length})
            </h2>
            <div className="space-y-6">
                {assignments.length > 0 && (
                    <div>
                        <h3 className="text-xl font-semibold text-gray-700 mb-3 flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-blue-500"></span>
                            New Assignments ({assignments.length})
                        </h3>
                        <div className="space-y-4 max-h-[30vh] overflow-y-auto pr-2">
                            {assignments.map(event => <EventCard key={event.recordId} event={event} />)}
                        </div>
                    </div>
                )}

                {overdueEvents.length > 0 && (
                    <div>
                        <h3 className="text-xl font-semibold text-gray-700 mb-3 flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-red-500"></span>
                            Overdue ({overdueEvents.length})
                        </h3>
                        <div className="space-y-4 max-h-[30vh] overflow-y-auto pr-2">
                            {overdueEvents.map(event => <EventCard key={event.commentId} event={event} />)}
                        </div>
                    </div>
                )}

                {upcomingEvents.length > 0 && (
                    <div>
                        <h3 className="text-xl font-semibold text-gray-700 mb-3 flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-yellow-500"></span>
                            Upcoming in next hour ({upcomingEvents.length})
                        </h3>
                        <div className="space-y-4 max-h-[30vh] overflow-y-auto pr-2">
                            {upcomingEvents.map(event => <EventCard key={event.commentId} event={event} />)}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default NotificationsTab;